import React, { useEffect, useState } from 'react';
import http from 'services/http';
import { alerta } from 'components/Alert/index';
import Pagination from 'components/Paginacao';
import 'components/Paginacao/style.css';
import ModalAcao from './ModalAcao';

const STATUS_OPCOES = [
    { value: '', label: 'Todos os status' },
    { value: 'pendente', label: 'Pendente' },
    { value: 'agendada', label: 'Agendada' },
    { value: 'aprovada', label: 'Aprovada' },
    { value: 'recusada', label: 'Recusada' },
];

const STATUS_BADGE = {
    pendente: 'warning',
    agendada: 'info',
    aprovada: 'success',
    recusada: 'danger',
};

const pageSize = 20; // mesmo valor do backend (paginate(20))

const formatarDataHora = (valor) =>
    valor ? new Date(valor).toLocaleString('pt-BR') : '—';

export default function Postagem() {
    const [postagens, setPostagens] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [operadores, setOperadores] = useState([]);

    // Filtros
    const [status, setStatus] = useState('');
    const [usuarioId, setUsuarioId] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');

    const [modalPostagem, setModalPostagem] = useState(null);
    const [processando, setProcessando] = useState(false);

    // Lista de operadores para o filtro.
    useEffect(() => {
        const carregarOperadores = async () => {
            try {
                const { data: { data: usuarios } } = await http.get('/usuarios', {
                    params: { nivel: 'operador', resumida: true },
                });
                setOperadores(usuarios ?? []);
            } catch (e) {
                // filtro de operador é opcional — silencia
            }
        };
        carregarOperadores();
    }, []);

    // Busca a lista sempre que a página mudar (e no mount).
    useEffect(() => {
        let ativo = true;

        const carregar = async () => {
            setLoading(true);
            try {
                const { data: { data: paginator } } = await http.get('/admin/postagens', {
                    params: {
                        page: currentPage,
                        status: status || undefined,
                        usuario_id: usuarioId || undefined,
                        data_inicio: dataInicio || undefined,
                        data_fim: dataFim || undefined,
                    },
                });

                if (!ativo) return;
                setPostagens(paginator.data ?? []);
                setTotal(paginator.total ?? 0);
            } catch (e) {
                if (ativo) alerta(e.response?.data ?? { success: false, message: 'Erro ao carregar postagens.' });
            } finally {
                if (ativo) setLoading(false);
            }
        };

        carregar();
        return () => { ativo = false; };
    }, [currentPage]);

    // Aplica filtros: volta para a página 1 (ou recarrega se já estiver nela).
    const filtrar = () => {
        if (currentPage === 1) {
            recarregar();
        } else {
            setCurrentPage(1);
        }
    };

    const limparFiltros = () => {
        setStatus('');
        setUsuarioId('');
        setDataInicio('');
        setDataFim('');
        if (currentPage === 1) {
            // recarrega sem filtros no próximo tick
            setTimeout(recarregarSemFiltros, 0);
        } else {
            setCurrentPage(1);
        }
    };

    // Recarrega usando os filtros atuais do state.
    const recarregar = async () => {
        setLoading(true);
        try {
            const { data: { data: paginator } } = await http.get('/admin/postagens', {
                params: {
                    page: 1,
                    status: status || undefined,
                    usuario_id: usuarioId || undefined,
                    data_inicio: dataInicio || undefined,
                    data_fim: dataFim || undefined,
                },
            });
            setPostagens(paginator.data ?? []);
            setTotal(paginator.total ?? 0);
        } catch (e) {
            alerta(e.response?.data ?? { success: false, message: 'Erro ao carregar postagens.' });
        } finally {
            setLoading(false);
        }
    };

    const recarregarSemFiltros = async () => {
        setLoading(true);
        try {
            const { data: { data: paginator } } = await http.get('/admin/postagens', { params: { page: 1 } });
            setPostagens(paginator.data ?? []);
            setTotal(paginator.total ?? 0);
        } catch (e) {
            alerta(e.response?.data ?? { success: false, message: 'Erro ao carregar postagens.' });
        } finally {
            setLoading(false);
        }
    };

    const mudarPagina = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const confirmarAcao = async (payload) => {
        if (!modalPostagem) return;
        setProcessando(true);
        try {
            const response = await http.patch(`/admin/postagens/${modalPostagem.id}`, payload);
            alerta(response.data);
            setModalPostagem(null);
            recarregar();
        } catch (e) {
            alerta(e.response?.data ?? { success: false, message: 'Erro ao processar a postagem.' });
        } finally {
            setProcessando(false);
        }
    };

    const detalheStatus = (p) => {
        if (p.status === 'aprovada') return `Aprovada em ${formatarDataHora(p.aprovada_em)}`;
        if (p.status === 'agendada') return `Aprova em ${formatarDataHora(p.agendar_para)}`;
        if (p.status === 'recusada') return p.motivo ? `Motivo: ${p.motivo}` : 'Recusada';
        return '—';
    };

    return (
        <>
            <ModalAcao
                postagem={modalPostagem}
                processando={processando}
                onClose={() => setModalPostagem(null)}
                onConfirmar={confirmarAcao}
            />

            <div className="titulo-principal">Postagens</div>

            {/* Filtros */}
            <div className="conteudo-principal mb-3">
                <div className="row m-0">
                    <div className="form-group col-md-3">
                        <label>Status</label>
                        <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                            {STATUS_OPCOES.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group col-md-3">
                        <label>Operador</label>
                        <select className="form-control" value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)}>
                            <option value="">Todos</option>
                            {operadores.map((u) => (
                                <option key={u.id} value={u.id}>{(u.login ?? u.nome ?? '').toUpperCase()}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group col-md-3">
                        <label>Data início</label>
                        <input type="date" className="form-control" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
                    </div>

                    <div className="form-group col-md-3">
                        <label>Data fim</label>
                        <input type="date" className="form-control" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
                    </div>
                </div>

                <div className="d-flex justify-content-end px-2">
                    <button type="button" className="btn btn-sm btn-outline-secondary mr-2" onClick={limparFiltros}>
                        Limpar
                    </button>
                    <button type="button" className="btn btn-sm btn-primary" onClick={filtrar}>
                        Filtrar
                    </button>
                </div>
            </div>

            {/* Lista */}
            <div className="conteudo-principal p-0">
                {loading ? (
                    <div className="p-4 text-center">Carregando...</div>
                ) : (
                    <table className="table table-sm text-center border mb-0 align-middle">
                        <thead className="thead-light">
                            <tr>
                                <th>Imagem</th>
                                <th>Texto</th>
                                <th>Operador</th>
                                <th>Status</th>
                                <th>Criada em</th>
                                <th>Detalhe</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {postagens.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-muted p-4">Nenhuma postagem encontrada.</td>
                                </tr>
                            ) : postagens.map((p) => (
                                <tr key={p.id}>
                                    <td>
                                        {p.imagem ? (
                                            <a href={p.imagem} target="_blank" rel="noopener noreferrer">
                                                <img
                                                    src={p.imagem}
                                                    alt="postagem"
                                                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }}
                                                />
                                            </a>
                                        ) : '—'}
                                    </td>
                                    <td style={{ maxWidth: 220, whiteSpace: 'normal', textAlign: 'left' }}>{p.texto}</td>
                                    <td>{p.autor?.nome ?? '—'}</td>
                                    <td>
                                        <span className={`badge badge-${STATUS_BADGE[p.status] ?? 'secondary'}`}>
                                            {p.status}
                                        </span>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatarDataHora(p.created_at)}</td>
                                    <td style={{ maxWidth: 240, whiteSpace: 'normal', textAlign: 'left', fontSize: '0.82rem' }}>
                                        {detalheStatus(p)}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {(p.status === 'pendente' || p.status === 'agendada') ? (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => setModalPostagem(p)}
                                            >
                                                Avaliar
                                            </button>
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {!loading && total > pageSize && (
                    <div className="d-flex justify-content-center py-2">
                        <Pagination
                            currentPage={currentPage}
                            totalCount={total}
                            pageSize={pageSize}
                            onPageChange={mudarPagina}
                        />
                    </div>
                )}
            </div>
        </>
    );
}
